import { Attention } from '../interfaces/attention';
import { DatabaseRecord } from '../mixins/database-record';
import { ActivityLog, HasActivityLogs } from '../mixins/has-activity-logs';
import { AgencyBlocCustomField } from './agency-bloc-custom-field';
import { AgencyBlocEntity } from './agency-bloc-entities';
import { AgencyBlocIndividual } from './agency-bloc-individual';
import { HasEmailAttribute, HasFullNameAttribute } from './search-results-record';

export class AgencyBlocPolicy extends DatabaseRecord(AgencyBlocCustomField(HasActivityLogs(AgencyBlocEntity(HasEmailAttribute(HasFullNameAttribute()))))) {
  public readonly $entityID = 'PolicyID';
  public readonly $entityType = 'Policy';
  public readonly $entityDateLabel = 'Date Issued';
  public attention?: Attention;
  // The unique identifier for the policy
  PolicyID = 0;

  // The unique identifier for the policy holder
  HolderID = 0;

  // Name of the policy holder
  HolderName = '';

  // The type of the policy holder (individual, company, etc.)
  HolderType = '';

  // The unique identifier for the agent associated with the policy
  AgentID = 0;

  // The name of the agent associated with the policy
  AgentName = '';

  // The ID of additional agent 1 associated with the policy
  AdditionalAgent1ID?: number;

  // The name of additional agent 1 associated with the policy
  AdditionalAgent1Name = '';

  // The compensation for additional agent 1 associated with the policy
  AdditionalAgent1Compensation = 0;

  // The ID of additional agent 2 associated with the policy
  AdditionalAgent2ID?: number;

  // The name of additional agent 2 associated with the policy
  AdditionalAgent2Name = '';

  // The compensation for additional agent 2 associated with the policy
  AdditionalAgent2Compensation = 0;

  // The ID of additional agent 3 associated with the policy
  AdditionalAgent3ID?: number;

  // The name of additional agent 3 associated with the policy
  AdditionalAgent3Name = '';

  // The compensation for additional agent 3 associated with the policy
  AdditionalAgent3Compensation = 0;

  // The ID of additional agent 4 associated with the policy
  AdditionalAgent4ID?: number;

  // The name of additional agent 4 associated with the policy
  AdditionalAgent4Name = '';

  // The compensation for additional agent 4 associated with the policy
  AdditionalAgent4Compensation = 0;

  // The ID of additional agent 5 associated with the policy
  AdditionalAgent5ID?: number;

  // The name of additional agent 5 associated with the policy
  AdditionalAgent5Name = '';

  // The compensation for additional agent 5 associated with the policy
  AdditionalAgent5Compensation = 0;

  // The unique identifier for the carrier associated with the policy
  CarrierID = 0;

  // The name of the carrier associated with the policy
  CarrierName = '';

  // The unique identifier for the policy status lookup
  StatusLookupID = 0;

  // The description of the policy status
  StatusDescript = '';

  // The ID of the agent who signed the policy
  SignedByAgentID = 0;

  // The name of the agent who signed the policy
  SignedByAgentName = '';

  // The ID of the account manager user associated with the policy
  AcctMgrUserID = 0;

  // The name of the account manager associated with the policy
  AcctMgrName = '';

  // The policy number assigned by the carrier to the policy
  PolicyNumber = '';

  // The renewal increase percentage for the policy
  RenewalIncrease = 0;

  // The number of lives covered by the policy
  Lives = 0;

  // Any notes or comments for the policy
  Notes = '';

  // The frequency at which commissions are paid for the policy
  CommissionFreq = 0;

  // The unique identifier for the client associated with the policy
  ClientID = 0;

  // Indicates whether the policy is active
  Active = false;

  // Indicates whether the policy has been deleted
  Deleted = false;

  // The unique identifier for the coverage type lookup
  CoverageTypeLookupID = 0;

  // The description of the coverage type
  CoverageTypeDescript = '';

  // The unique identifier for the project code lookup
  ProjectCodeLookupID = 0;

  // The description of the project code
  ProjectCodeDescript = '';

  // The date the policy application was submitted
  AppSubmitDate = new Date();

  // The effective date of the policy
  EffectiveDate = new Date();

  // The renewal date of the policy
  RenewalDate = new Date();

  // The termination date of the policy
  TermDate = new Date();

  // The total premium amount for the policy
  Premium = 0;

  // The effective premium amount for the policy
  EffectivePremium = 0;

  // The submitted premium amount for the policy
  SubmittedPremium = 0;

  // The placement premium amount for the policy
  PlacementPremium = 0;

  // The frequency at which premiums are paid for the policy
  PayFreq = '';

  // The coverages included in the policy
  coverages: {
    PolicyCoverageID: number;
    CarrierProduct: string;
  }[] = [];

  /**
   * The owner of the policy
   */
  public individual = new AgencyBlocIndividual();

  /**
   * Get the entity date for this Model. Usually used for sorting and displaying in the UI.
   */
  public get entityDate(): Date {
    return new Date(this.AppSubmitDate);
  }

  /**
   * Get the entity ID for this Model. Usually used for sorting and displaying in the UI.
   */
  public get entityId(): number {
    return this.PolicyID;
  }

  public actual_premium?: number;
  public credited?: number;
  constructor(data?: AgencyBlocPolicy) {
    super(data);
    Object.assign(this, data);

    if (data) {
      if (data.created_at) this.created_at = new Date(data.created_at);
      if (data.updated_at) this.updated_at = new Date(data.updated_at);
      if (data.deleted_at) this.deleted_at = new Date(data.deleted_at);
      if (data.logs) this.logs = data.logs.map((log) => new ActivityLog(log));
      if (data.individual) this.individual = new AgencyBlocIndividual(data.individual);

      this.$entityId = this.PolicyID;
    }
  }
}

export type POLICY_INFO_TYPE = {
  Carrier?: string;
  'Policy Number'?: string;
  Status?: string;
  'Coverage Type'?: string;
  Premium?: number;
  'Pay Frequency'?: string;
  Lives?: number;
  'Project Code'?: string;
};

export type POLICY_DATES_TYPE = {
  'App Submit Date'?: Date;
  'Effective Date'?: Date;
  'Renewal Date'?: Date;
  'Term Date'?: Date;
  'Issue Date'?: Date;
  'Policy Date'?: Date;
};

export type AGENT_INFO_TYPE = {
  'Signed By'?: string;
  'Account Manager'?: string;
  'Servicing Agent'?: string;
  'Additional Agent 1'?: string;
  'Additional Agent 2'?: string;
};
export type INDIVIDUAL_POLICY_TYPE = {
  'Pay Method'?: string;
  Notes?: string;
};
export type ELECTION_TYPE = {
  'Member ID'?: string;
  Election?: string;
  'Who is Covered'?: string;
  Notes?: string;
};

export type SEARCH_POLICY = {
  policyID?: number;
  policyCoverageType?: string;
  policyStatus?: string;
  entityTypeID?: string;
  entityID?: number;
  carrier?: string;
};
